import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import type { TextFieldProps } from '@mui/material';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import { getSelectorAttribute, usagePageSelectors } from '@vy/app-selectors';
import type { VyDate } from '@vy/utils';
import { isNotNullOrUndefined, vyDate } from '@vy/utils';
import React from 'react';
import { useFromDayParam } from '../../../../hooks/search-params/use-from-day-param';
import { useToDayParam } from '../../../../hooks/search-params/use-to-day-param';
import { useEffectOnce } from '../../../../hooks/use-effect-once';
import { VyRangePickerMenu } from './VyRangePickerMenu';

type DataMeteringRangePickerProps = {
  size?: 'small' | 'medium';
  textFieldProps?: TextFieldProps;
};

export const VyRangePicker: React.FC<DataMeteringRangePickerProps> = ({ size = 'small', textFieldProps }) => {
  const [fromDay, setFromDay] = useFromDayParam();
  const [toDay, setToDay] = useToDayParam();

  const onStartDateChange = (startDate: VyDate | null) => {
    if (isNotNullOrUndefined(startDate)) {
      setFromDay(startDate.startOf('day'));

      if (isNotNullOrUndefined(toDay) && toDay.isBefore(startDate)) {
        const endOfSelectedDay = vyDate(startDate).endOf('day');
        setToDay(endOfSelectedDay);
      }
    } else {
      setFromDay(null);
    }
  };

  const onEndDateChange = (endDate: VyDate | null) => {
    if (isNotNullOrUndefined(endDate)) {
      setToDay(endDate.endOf('day'));

      if (isNotNullOrUndefined(fromDay) && endDate.isBefore(fromDay)) {
        setFromDay(null);
      }
    } else {
      setToDay(null);
    }
  };

  useEffectOnce(() => {
    onStartDateChange(fromDay);
    onEndDateChange(toDay);
  });

  return (
    <div className="flex items-center gap-6">
      <div className="flex items-center gap-2">
        <VyRangePickerMenu
          fromDate={fromDay}
          onEndDateChange={onEndDateChange}
          onStartDateChange={onStartDateChange}
          toDate={toDay}
          disabled={textFieldProps?.disabled}
        >
          <TextField
            {...getSelectorAttribute(usagePageSelectors.header.rangeSelectButton)}
            label="Date Range"
            value={fromDay && toDay ? `${fromDay.format('MMM DD')} - ${toDay.format('MMM DD')}` : ''}
            size={size}
            {...textFieldProps}
            sx={{
              paddingBottom: 0,
              '.css-3qiyqj-MuiInputBase-root-MuiFilledInput-root': {
                height: '36px',
              },
              '& .css-1ssscdh-MuiInputBase-input-MuiFilledInput-input': {
                paddingBottom: '5px',
              },
              width: '240px',
              ...textFieldProps?.sx,
            }}
            slotProps={{
              input: {
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton>
                      <ArrowDropDownIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }
            }} />
        </VyRangePickerMenu>
      </div>
    </div>
  );
};
